import  normalize from 'json-api-normalize';
import { MediaPathHelper } from '../helpers/media-path.helper';
import { AddressModel } from './address.model';
import { DrupalBaseModel } from './drupal-base.model';
import { IndividualModel } from './individual.model';
import { ProgramModel } from './program.model';
import { SpecialtyModel } from './specialty.model';
import dayjs from 'dayjs';


export class VacantPositionModel extends DrupalBaseModel {
  private static readonly dateDisplayFormat = 'MM-DD-YYYY';

  public static parseVacantPosition(vacantPositionJson: any, baseImageUrl: string): VacantPositionModel | VacantPositionModel[] {
    const normalizedData = normalize(vacantPositionJson).get([
      'drupal_internal__nid',
      'path',
      'id',
      'title',
      'changed',
      'field_acgme_accredited',
      'field_address',
      'field_application_deadline',
      'field_geolocation',
      'field_prerequisites',
      'field_program_non_acgme',
      'field_program_year',
      'field_special_comments',
      'field_start_date',
      'field_contact',
      'field_program',
      'field_region',
      'field_specialty',
      'field_image',
      'moderation_state',
      'metatag_normalized'
    ]);

    return Array.isArray(normalizedData) ?
      normalizedData.map(vp => this.setObjValues(vp, baseImageUrl)) :
      this.setObjValues(normalizedData, baseImageUrl);
  }

  private static setObjValues (normalizedData, baseImageUrl): VacantPositionModel {
    return new VacantPositionModel({
      nid: normalizedData.drupal_internal__nid ? normalizedData.drupal_internal__nid : null,
      cmsPath: normalizedData.path ? normalizedData.path : null,
      uuid: normalizedData.id ? normalizedData.id : null,
      title: normalizedData.title ? normalizedData.title : null,
      changed: normalizedData.changed ? normalizedData.changed : null,
      acgmeAccredited: normalizedData.field_acgme_accredited ? normalizedData.field_acgme_accredited : null,
      metatags: normalizedData.metatag_normalized ? normalizedData.metatag_normalized : '',
      address: normalizedData.field_address
        ? new AddressModel({
          organization: normalizedData.field_address.organization ? normalizedData.field_address.organization : null,
          address1: normalizedData.field_address.address_line1 ? normalizedData.field_address.address_line1 : null,
          address2: normalizedData.field_address.address_line2 ? normalizedData.field_address.address_line2 : null,
          city: normalizedData.field_address.locality ? normalizedData.field_address.locality : null,
          state: normalizedData.field_address.administrative_area ? normalizedData.field_address.administrative_area : null,
          zipCode: normalizedData.field_address.postal_code ? normalizedData.field_address.postal_code : null,
          region: normalizedData.field_address.region ? normalizedData.field_address.region : null,
          countryCode: normalizedData.field_address.country_code ? normalizedData.field_address.country_code : null,
        })
        : new AddressModel(),
      applicationDeadline: normalizedData.field_application_deadline ? this.formatDate(normalizedData.field_application_deadline) : null,
      geoLocation: normalizedData.field_geolocation ? normalizedData.field_geolocation : null,
      prerequisites: normalizedData.field_prerequisites ? normalizedData.field_prerequisites.value : null,
      programNonACGME: normalizedData.field_program_non_acgme ? normalizedData.field_program_non_acgme : null,
      pgy: normalizedData.field_program_year ? normalizedData.field_program_year : null,
      specialComments: normalizedData.field_special_comments ? normalizedData.field_special_comments.value : null,
      startDate: normalizedData.field_start_date ? this.formatDate(normalizedData.field_start_date) : null,
      contact: normalizedData.field_contact ? IndividualModel.parseIndividual(normalizedData.field_contact.dataset) : null,
      program: normalizedData.field_program ? new ProgramModel({
        cmsPath: normalizedData.field_program.dataset.data.attributes.path ? normalizedData.field_program.dataset.data.attributes.path : null,
        nid: normalizedData.field_program.dataset.data.attributes.drupal_internal__nid ? normalizedData.field_program.dataset.data.attributes.drupal_internal__nid : null,
        programNumber: normalizedData.field_program.dataset.data.attributes.field_program_id ? normalizedData.field_program.dataset.data.attributes.field_program_id : null,
        programName: normalizedData.field_program.dataset.data.attributes.title ? normalizedData.field_program.dataset.data.attributes.title : null,
        uuid: normalizedData.field_program.dataset.data.id ? normalizedData.field_program.dataset.data.id : null,
      }) : null,
      specialty:  normalizedData.field_specialty
        ? new SpecialtyModel(
        {
          nid: normalizedData.field_specialty.dataset.data.attributes.drupal_internal__nid ? normalizedData.field_specialty.dataset.data.attributes.drupal_internal__nid : null,
          uuid: normalizedData.field_specialty.dataset.data.id ? normalizedData.field_specialty.dataset.data.id : null,
          name: normalizedData.field_specialty.dataset.data.attributes.title ? normalizedData.field_specialty.dataset.data.attributes.title : null,
          link: normalizedData.field_specialty.dataset.data.attributes.field_link ?
            normalizedData.field_specialty.dataset.data.attributes.field_link.uri : null,
          path: normalizedData.field_specialty.dataset.data.attributes.path ?
            normalizedData.field_specialty.dataset.data.attributes.path : null
        })
        : new SpecialtyModel(),
      imageUrl: normalizedData.field_image && normalizedData.field_image.dataset.included.filter(f => f.type === 'file--file')[0] ?
        MediaPathHelper.imageSrcUrl(
          normalizedData.field_image.dataset.included.filter(f => f.type === 'file--file')[0].attributes.uri.url, baseImageUrl) : null,
      moderationState: normalizedData.moderation_state,
    });
  }

  private static formatDate(dateValue, formatTo = VacantPositionModel.dateDisplayFormat, formatFrom = 'YYYY-MM-DD') {
    if (dateValue) {
      return dayjs(dateValue, formatFrom).format(formatTo);
    }

    return '';
  }

  public uuid: string;
  public acgmeAccredited: boolean;
  public applicationDeadline: string;
  public pgy: number;
  public prerequisites: string;
  public programNonACGME: string;
  public startDate: string;
  public specialComments: string;
  public title: string;
  public changed: string;
  public geoLocation: any;
  public address: AddressModel;
  public contact: IndividualModel;
  public program: ProgramModel;
  public specialty: SpecialtyModel;
  public imageUrl: string;
  public moderationState: string;
  public metatags: any;


  constructor(values?: Partial<VacantPositionModel>) {
    super(values);
    Object.assign(this, values);
  }

  get inputDateFormat(): string {
    return VacantPositionModel.dateDisplayFormat;
  }

  get city(): string {
    return this.acgmeAccredited && this.contact ? this.contact.address.city : this.address.city;
  }

  get name(): string {
    return this.acgmeAccredited && this.program ? this.program.programName : this.title;
  }

  get programNumber(): string {
    return this.acgmeAccredited && this.program ? this.program.programNumber : '';
  }

  get state(): string {
    return this.acgmeAccredited && this.contact ? this.contact.address.state : this.address.state;
  }

  get region(): string {
    return this.acgmeAccredited && this.contact ? this.contact.address.region : this.address.region;
  }

  get info(): string[] {
    const startDate = this.startDate === null ? '' : this.startDate.split('-').join('/');
    const applicationDeadline = this.applicationDeadline === null ? '' : this.applicationDeadline.split('-').join('/');
    return [
      this.acgmeAccredited ? 'ACGME' : 'Non-ACGME',
      'Program Year ' + this.pgy,
      'Start Date: ' + startDate,
      'Application Deadline: ' + applicationDeadline
    ];
  }
}
